import { actionableCount } from "@/ui/todayCounts";
import { NA } from "@/ui";
import { useEffect, useMemo, useState } from "react";
import { todayQueue } from "@/api/tools";
import { useRead } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { Banner, Empty, Skeleton } from "@/ui";
import { SECTIONS, SECTION_LABEL, SECTION_TONE, sectionFromHash, type SectionKey } from "@/views/today/chips";
import { FAMILY_EMPTY, FAMILY_LABEL, byFamily } from "@/views/today/families";
import { RowItem, Section } from "@/views/today/Rows";

// V1-INTERACTIONS §4.1 — one list of what needs this person: Attention Required · Overdue · Pending Others ·
// Upcoming · Recent. All groups the outstanding work by family (Interviews · Debriefs · Feedback · Everything else)
// and never carries a completed row; Recent is its own chip.
export function Today() {
  const { me } = useSession();
  const { register } = useViewState();
  const [chip, setChip] = useState<SectionKey>(() => sectionFromHash(location.hash));
  const filter = (value: SectionKey) => { setChip(value); history.replaceState(null, "", `#today?filter=${value}`); };
  const [showRecent, setShowRecent] = useState(false);
  const q = useRead(["today"], () => todayQueue({}), { refetchInterval: 60_000 });
  useEffect(() => register({ view: "today", ids: {}, summary: `today for ${me?.roles.join(",")} · badge ${q.data?.badge ?? "?"}` }), [register, me, q.data?.badge]);
  const d = q.data;
  const counts: Record<string, number> | undefined = d ? { ...d.counts, needs_you: actionableCount(d) } : undefined;
  // EM-24: All is every OUTSTANDING row and nothing that is already done — `recent` is not in this list.
  const outstanding = useMemo(() => (d ? [...d.overdue, ...d.needs_you, ...d.waiting, ...d.upcoming] : []), [d]);
  const families = useMemo(() => byFamily(outstanding), [outstanding]);

  return (
    <div>
      <h1 className="page">Today</h1>
      <p className="lede">
        {me?.name} · {me?.role_labels.join(" · ")}
      </p>
      {q.isError && <Banner error={q.error} onRetry={() => void q.refetch()} />}
      {q.isLoading && <Skeleton rows={4} />}
      {d && (
        <>
          <div className="grid grid--kpi">{d.tiles.map(tile => <a key={tile.key} className={`card kpi kpi--link${tile.count === 0 ? " kpi--zero" : ""}`} href={tile.route} onClick={event => {
            if (tile.route.startsWith("#today?filter=")) { event.preventDefault(); filter(tile.route.split("=")[1] as SectionKey); }
          }}>
            <b>{tile.count ?? <NA />}</b><span>{tile.label}</span>{tile.note && <p className="muted">{tile.note}</p>}
          </a>)}</div>
          <div className="row chips" role="group" aria-label="Sections">
            {SECTIONS.map((s) => (
              <button key={s} type="button" className={"chip " + SECTION_TONE[s] + (chip === s ? " chip--on" : "") + (s !== "all" && counts && !counts[s] ? " chip--zero" : "")} aria-pressed={chip === s} onClick={() => filter(s)}>
                {SECTION_LABEL[s]}
                {s !== "all" && counts ? <span className="chip__n">{counts[s] ?? 0}</span> : null}
              </button>
            ))}
          </div>
          {chip === "all" && (families.length === 0
            ? <Empty level={2} heading="Today is clear">{`Nothing needs your attention. ${d.waiting.length} ${d.waiting.length === 1 ? "item is" : "items are"} pending on others · ${d.upcoming.length} upcoming this week.`}</Empty>
            : families.map(({ family, rows }) => <Section key={family} title={FAMILY_LABEL[family]} count={rows.length} rows={rows} empty={FAMILY_EMPTY[family]} />))}
          {chip === "needs_you" && (
            <Section title={SECTION_LABEL.needs_you} count={actionableCount(d)} rows={[...d.overdue, ...d.needs_you]} empty={`Nothing needs your attention. ${d.waiting.length} ${d.waiting.length === 1 ? "item is" : "items are"} pending on others · ${d.upcoming.length} upcoming this week.`} />
          )}
          {chip === "overdue" && <Section title={SECTION_LABEL.overdue} count={counts?.overdue ?? 0} rows={d.overdue} empty="Nothing is overdue." />}
          {chip === "waiting" && <Section title={SECTION_LABEL.waiting} count={counts?.waiting ?? 0} rows={d.waiting} empty="Nothing is pending on others." />}
          {chip === "upcoming" && <Section title={SECTION_LABEL.upcoming} count={counts?.upcoming ?? 0} rows={d.upcoming} empty="Nothing is upcoming this week." />}
          {chip === "recent" && (
            <section className="recent">
              <button className="recent__toggle" aria-expanded={showRecent} aria-controls="recent-items" onClick={() => setShowRecent(!showRecent)}>Recent — {d.recent.length} in the last 7 days <span aria-hidden="true">{showRecent ? "−" : "+"}</span></button>
              <div id="recent-items" hidden={!showRecent}>
              {d.recent.length === 0 ? <Empty level={2} heading="Recent activity is clear">No notices in the last seven days.</Empty> : <ul className="rows">{d.recent.map((r) => <RowItem key={r.id} row={r} />)}</ul>}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
